import React from 'react';
import { argsToStringifiable } from '@local/finsera-core/src/helpers/embed/utils';
import { _, moment, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import Chart from './chart';
import { useUiStyles } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';

type ScatterPlotChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  readOnly: boolean;
  xAxisField: string;
  yAxisField: string;
  xAxisLabel: string;
  yAxisLabel: string;
  alert?: {
    type: 'error' | 'warning';
    message: string;
  };

  category?: string;
  exportTitle?: string;
  exportSubTitle?: string;
  download?: boolean;
  setExternalEmbed?: (_data: types.analysis.EmbedChartData) => void;
  numberFormat?: string;
  weightField?: string;
  title?: string;
  hideLegend?: boolean;
  elementTooltipName?: string;
  elementTooltipText?: string;
  elementBulletName?: string;
  lineOverlay?: 'trendline' | 'x=y' | 'fit';
  size?: 'medium' | 'large';
  lineSeriesName?: string;
  xAxisFormatter?: string;
  yAxisFormatter?: string;
  xIsDate?: boolean;
};

const ScatterPlotChart = (args: ScatterPlotChartProps) => {
  const uiStyles = useUiStyles();

  const {
    id,
    data,
    fullScreen,
    readOnly,
    category,
    xAxisField,
    yAxisField,
    xAxisLabel,
    yAxisLabel,
    elementTooltipName,
    elementTooltipText,
    numberFormat = '#.##%',
    alert,
    exportTitle,
    exportSubTitle,
    download = true,
    setExternalEmbed,
    weightField,
    title,
    hideLegend = false,
    elementBulletName = undefined,
    lineOverlay = undefined,
    size = 'medium',
    lineSeriesName,
    xAxisFormatter,
    yAxisFormatter,
    xIsDate = false,
  } = args;

  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (setExternalEmbed)
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'scatter-plot-chart',
          args: argsToStringifiable({
            ...args,
            data,
          }),
        });
      if (xIsDate) {
        setLocalData(
          data.map((row) => ({
            ...row,
            [xAxisField]: moment(row[xAxisField] as string).valueOf(),
          }))
        );
      } else {
        setLocalData(data);
      }
    };

    handleData();
  }, [data]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: `${alert ? 'auto ' : ''} 1fr`,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}
      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box sx={{ height: fullScreen ? 'calc(100% - 40px)' : '100%' }}>
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <Chart
              id={id}
              data={localData}
              fullScreen={fullScreen}
              download={download}
              category={category}
              xAxisField={xAxisField}
              yAxisField={yAxisField}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              numberFormat={numberFormat}
              elementTooltipName={elementTooltipName}
              elementTooltipText={elementTooltipText}
              weightField={weightField}
              hideLegend={hideLegend}
              elementBulletName={elementBulletName}
              lineOverlay={lineOverlay}
              chartSize={size}
              lineSeriesName={lineSeriesName}
              xAxisFormatter={xAxisFormatter}
              yAxisFormatter={yAxisFormatter}
              title={title}
              xIsDate={xIsDate}
            />
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default ScatterPlotChart;
